
import React, { useState, useRef, useEffect } from 'react';
import { HelpCircle, X, User, Briefcase, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const TserviAssistant = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [flow, setFlow] = useState(null);
    const [messages, setMessages] = useState([]);
    const scrollRef = useRef(null);
    const location = useLocation();

    const isProfilePage = location.pathname.includes('/profile');

    const respostas = {
        cliente: {
            "Como encontrar um prestador?": "1. Na tela inicial, use a busca ou selecione uma categoria.\n2. Filtre por cidade ou use o GPS para ver quem está perto.\n3. Clique no perfil e mande uma mensagem direta.",
            "Como contratar alguém?": "Combine o preço e a data diretamente no chat. O T-SERVI facilita a conexão, mas o acerto é feito entre você e o profissional.",
            "O que é raio de atuação?": "É a distância que o prestador se dispõe a viajar. Assim você só vê quem realmente atende na sua localização.",
            "Preciso pagar pelo app?": "Não! O uso para busca e contato é gratuito. Você paga apenas o valor do serviço combinado com o profissional."
        },
        servi: {
            "Como cadastrar meu serviço?": "1. Vá em 'Meu Perfil' e complete seus dados.\n2. Defina sua categoria e raio de atendimento.\n3. Adicione fotos reais do seu trabalho no Portfólio.",
            "Qual raio de atuação colocar?": "Pense na distância que você consegue percorrer no dia a dia. Comece com 20-30km e ajuste conforme a demanda.",
            "Dicas para o portfólio": "Use fotos claras e de serviços finalizados. Profissionais com boas fotos e avaliações aparecem primeiro nas buscas.",
            "Como recebo pagamentos?": "Você combina a forma de pagamento (Pix, dinheiro, etc) diretamente com o cliente. Em breve teremos pagamentos integrados."
        }
    };

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, flow]);

    const initChat = () => {
        if (messages.length === 0) {
            setMessages([{ type: 'bot', text: "Olá! Como posso te ajudar hoje?" }]);
        }
        setIsOpen(true);
        setIsMinimized(false);
    };

    const startFlow = (type) => {
        setFlow(type);
        setMessages(prev => [
            ...prev,
            { type: 'user', text: type === 'cliente' ? 'Sou Cliente' : 'Sou SERVI' },
            { type: 'bot', text: "Escolha uma dúvida abaixo:" }
        ]);
    };

    const sendReply = (pergunta) => {
        setMessages(prev => [
            ...prev,
            { type: 'user', text: pergunta },
            { type: 'bot', text: respostas[flow][pergunta] }
        ]);
    };

    if (isProfilePage) return null;

    if (!isOpen) {
        return (
            <div
                className={`absolute bottom-20 transition-all duration-500 z-[9999] ${isMinimized ? '-left-6' : 'left-4'}`}
            >
                <button
                    onClick={initChat}
                    className="w-10 h-10 bg-white border-2 border-green-600 text-green-600 rounded-full shadow-lg flex items-center justify-center hover:bg-green-50 active:scale-95 relative group"
                >
                    <HelpCircle size={20} />
                </button>
                <button
                    onClick={() => setIsMinimized(!isMinimized)}
                    className="absolute -top-2 -right-2 bg-gray-200 text-gray-500 rounded-full p-0.5 hover:bg-gray-300"
                >
                    {isMinimized ? <ChevronRight size={10} /> : <X size={10} />}
                </button>
            </div>
        );
    }

    return (
        <div className="absolute bottom-24 left-4 right-4 max-w-[280px] bg-white rounded-[2rem] shadow-2xl z-[10000] flex flex-col overflow-hidden border border-gray-100 animate-slide-up">
            <div className="bg-green-600 p-3 text-white flex justify-between items-center">
                <div className="flex items-center space-x-2 px-2">
                    <span className="font-black text-[10px] uppercase tracking-widest">Suporte Beta</span>
                </div>
                <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 p-1 rounded-full"><X size={16} /></button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 bg-gray-50 space-y-3 max-h-[300px] no-scrollbar">
                {messages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[90%] p-3 rounded-2xl text-[11px] font-medium leading-relaxed ${
                            msg.type === 'user' ? 'bg-green-600 text-white rounded-br-none' : 'bg-white text-gray-700 shadow-sm border border-gray-100 rounded-bl-none'
                        }`}>{msg.text}</div>
                    </div>
                ))}
            </div>

            <div className="p-3 bg-white border-t border-gray-50">
                {!flow ? (
                    <div className="flex space-x-2">
                        <button onClick={() => startFlow('cliente')} className="flex-1 py-2 bg-blue-50 text-blue-700 rounded-xl text-[10px] font-black uppercase hover:bg-blue-100 transition-colors">Cliente</button>
                        <button onClick={() => startFlow('servi')} className="flex-1 py-2 bg-green-50 text-green-700 rounded-xl text-[10px] font-black uppercase hover:bg-green-100 transition-colors">SERVI</button>
                    </div>
                ) : (
                    <div className="space-y-1.5">
                        <button onClick={() => setFlow(null)} className="flex items-center text-[9px] font-black text-gray-400 uppercase mb-1 hover:text-gray-600"><ChevronLeft size={10} /> Voltar</button>
                        {Object.keys(respostas[flow]).map(pergunta => (
                            <button key={pergunta} onClick={() => sendReply(pergunta)} className="w-full p-2 text-left bg-gray-50 hover:bg-green-50 rounded-lg text-[10px] font-bold text-gray-600 border border-transparent hover:border-green-100 transition-all leading-tight">{pergunta}</button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TserviAssistant;
